import {
  BaseError,
  UnauthorizedError,
  BaseHTTPError,
  BadRequestError,
  UnhandledError,
  ForbiddenError,
} from './';
import {
  BaseErrorConfig,
  BaseHttpErrorEventConfig,
  ErrorUnauthorizedConfig,
  FeedbackUIAction,
  FeedbackUITypes,
  publishBaseErrorEvent,
  publishBaseHttpErrorEvent,
  publishUnauthorizedEvent,
  publishUnhandledErrorEvent,
} from 'fe-core-events';

export type TErrorEventMap = {
  UnauthorizedError?: {
    config?: ErrorUnauthorizedConfig;
    action?: { event: string; text: { title: string } };
  };
  BaseHTTPError?: {
    config?: BaseHttpErrorEventConfig;
    action?: { event: string };
  };
  BaseError?: {
    config?: BaseHttpErrorEventConfig;
    action?: { event: string };
  };
  UnhandledError?: {
    config?: BaseErrorConfig;
    action?: { event: string };
  };
};

const logSilently = (errorType: string, error: Error) => {
  console.warn(`UseError - ${errorType} is handled silently`, error);
};

export type THandleCoreError = {
  feedbackUIAvailability: boolean;
  consumer: string;
  error: Error | null;
  errorMap?: TErrorEventMap;
  errorMapOverride?: {
    type?: FeedbackUITypes;
    action?: FeedbackUIAction;
    handleAs?: 'default' | 'silent' | 'ignore';
  };
};
export const useCoreError = () => {
  const handleCoreError = ({
    feedbackUIAvailability,
    consumer,
    error,
    errorMap,
    errorMapOverride,
  }: THandleCoreError) => {
    const overrideHandleErrorAs = errorMapOverride?.handleAs || 'default';
    const overrideType = errorMapOverride?.type || 'modal';
    const overrideAction = errorMapOverride?.action || {};

    if (!error || overrideHandleErrorAs === 'ignore') {
      return;
    }

    if (!feedbackUIAvailability) {
      console.error(
        `Feedback UI is not available for application ${consumer}`,
        error,
      );
    } else {
      console.info('Feedback UI is available');
    }

    switch (true) {
      case error instanceof UnauthorizedError:
        if (overrideHandleErrorAs === 'silent') {
          return logSilently('UnauthorizedError', error);
        }

        publishUnauthorizedEvent({
          consumer,
          event: 'feedback.ui.error.unauthorized',
          status: 'error',
          type: overrideType || 'modal',
          config: {
            title: error.title,
            description: error.description,
          },
          ...(errorMap?.UnauthorizedError || {}),
          ...(overrideAction || {}),
        });
        break;

      case error instanceof BaseHTTPError:
        if (overrideHandleErrorAs === 'silent') {
          return logSilently('BaseHTTPError', error);
        }

        publishBaseHttpErrorEvent({
          consumer,
          event: 'feedback.ui.error.baseHttp',
          status: 'error',
          type: overrideType || 'modal',
          httpStatusCode: error.httpCode,
          config: {
            title: error.title,
            description: error.description,
          },
          ...(errorMap?.BaseHTTPError || {}),
          ...(overrideAction || {}),
        });
        break;

      case error instanceof BaseError:
        if (overrideHandleErrorAs === 'silent') {
          return logSilently('BaseError', error);
        }

        publishBaseErrorEvent({
          consumer,
          event: 'feedback.ui.error.base',
          status: 'error',
          type: overrideType || 'modal',
          config: {
            title: error.title,
            description: error.description,
          },
          ...(errorMap?.BaseError || {}),
          ...(overrideAction || {}),
        });
        break;

      case error instanceof BadRequestError:
        if (overrideHandleErrorAs === 'silent') {
          return logSilently('BadRequestError', error);
        }

        publishBaseErrorEvent({
          consumer,
          event: 'feedback.ui.error.base',
          status: 'error',
          type: overrideType || 'modal',
          config: {
            title: error.title,
            description: error.description,
          },
          ...(overrideAction || {}),
        });
        break;

      case error instanceof ForbiddenError:
        if (overrideHandleErrorAs === 'silent') {
          return logSilently('ForbiddenError', error);
        }

        publishBaseErrorEvent({
          consumer,
          event: 'feedback.ui.error.base',
          status: 'error',
          type: overrideType || 'modal',
          config: {
            title: error.title,
            description: error.description,
          },
          ...(overrideAction || {}),
        });
        break;

      default:
        if (overrideHandleErrorAs === 'silent') {
          logSilently('Error', error);
        } else {
          const unhandledError = error as UnhandledError;
          publishUnhandledErrorEvent({
            consumer,
            event: 'feedback.ui.error.unhandled',
            status: 'error',
            type: overrideType || 'modal',
            config: {
              title: unhandledError.title || 'Unhandled error',
              description: unhandledError.description || 'Unhandled error',
            },
            ...(errorMap?.UnhandledError || {}),
            ...(overrideAction || {}),
          });
          break;
        }
    }
  };

  return handleCoreError;
};
